import EventDispatcher from "../../../../../shared/lib/html5-software-library/src/ranj/util/EventDispatcher";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import GameConfig from "./GameConfig";
import GameStatus from "./GameStatus";
import { getSeededPRNG } from "../../../../../shared/src/ranj/util/RandomUtil";
import LevelEndType from "../data/LevelEndType";
import ProgressValue from "../../../../../shared/src/ranj/equalture/platform/ProgressValue";
import PuzzleModel from "./PuzzleModel";
import SaveEvent from "../../../../../shared/src/ranj/equalture/savegame/SaveEvent";
import TrackingController from "./TrackingController";
export default class GameModel extends EventDispatcher {

    /**
     * @param {GameConfig} gameConfig 
     * @param {TrackingController} trackingController 
     * @param {AssetLibrary} assetLibrary
     */
    constructor(dispatcher, gameConfig, trackingController, assetLibrary) {
        super();
        this._contextDispatcher = dispatcher;
        this._config = gameConfig;
        this._trackingController = trackingController;
        this._assetLibrary = assetLibrary;

        const speedParam = parseFloat(getURLParameter("speed"));
        this._speedfactor = isNaN(speedParam) ? 1 : speedParam;
        this.lastPuzzleSuccessful = false;
        this.tutorialAlreadySeen = false;
    }

    // Initialization Methods
    initialize() {
        this.reset();
    }

    reset() {
        this._puzzleIndex = -1;
        this._sequenceIndex = 0;
        this._elapsedTime = 0;
        this._initializeSeed();
        this._initializeConfig();

    }

    // Initialization Methods
    _initializeSeed() {
        const urlSeed = getURLParameter("seed");
        this.seed = urlSeed !== null ? parseInt(urlSeed) : Math.floor(Math.random() * 89999) + 10000;
        console.warn("Initialization of GameModel with seed: " + this.seed);
    }

    _initializeConfig() {
        var configJSON = this._assetLibrary.getAsset("gameConfig");
        this._config.setPropertiesFromObject(configJSON);
        this.dispatchEvent(GameModel.CONFIG_LOADED);
    }

    // Game Flow Control Methods
    startPractice() {
        this.elapsedTime = 0;
        this.status = GameStatus.PRACTICE;
        this._puzzleIndex = -1;
        this.doNextPuzzle();
    }

    start() {
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this.status = GameStatus.RUNNING;
        this._puzzleIndex = -1;
        this.doNextPuzzle();
    }

    resumeAfterRestore() {
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this.initPuzzle(this._puzzleIndex, this._sequenceIndex);
    }

    end() {
        this.dispatchPlatformProgressEvent(ProgressValue.COMPLETED);
        this.status = GameStatus.END;
    }

    step(dt) {
        this.elapsedTime += dt;
        if (this._timerRunning) {
            if (this.remainingTime <= 0) {
                this.timeout();
            } else if (this._elapsedTime - this._lastTimerUpdate > this._config.getValue("timings.delayRefreshTimer")) {
                this.updateTimer();
            }
        }
    }

    // Puzzle Management Methods
    doNextPuzzle() {
        this.initPuzzle(this._puzzleIndex + 1, 0);
    }

    initPuzzle(index, sequenceIndex = 0) {
        this._destroyPuzzle();
        const nbrPuzzles = this.status == GameStatus.PRACTICE ? this._config.practicePuzzles.length : this._config.puzzles.length;
        this._puzzleIndex = (index >= nbrPuzzles ? 0 : index);
        this._sequenceIndex = sequenceIndex;
        this._puzzleSubmitted = false;

        const puzzleCfg = this.status == GameStatus.PRACTICE ? this._config.getPracticePuzzle(index) : this._config.getPuzzle(index);
        this._puzzleModel = this._createPuzzleModel(puzzleCfg, sequenceIndex);

        this.lastPuzzleSuccessful = false;
        this._addPuzzleEventListeners();
        this.dispatchEvent(GameModel.PUZZLE, this._puzzleModel);
    }

    _createPuzzleModel(puzzleCfg, sequenceIndex = 0) {
        const ruleId = puzzleCfg.rule.id[sequenceIndex];
        const sequence = puzzleCfg.sequence[sequenceIndex];
        const hasNextSequence = sequenceIndex < puzzleCfg.sequence.length - 1;
        const correctInput = puzzleCfg.rule.correctInput[sequenceIndex];
        const ordered = puzzleCfg.rule.correctOrderRequired;

        return new PuzzleModel(
            puzzleCfg.location,
            puzzleCfg.allowFail,
            puzzleCfg.successFeedback,
            puzzleCfg.time,
            ruleId,
            sequence,
            ordered,
            hasNextSequence,
            correctInput
        );
    }

    _destroyPuzzle() {
        if (this._puzzleModel) {
            this._puzzleModel.removeEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
            this._puzzleModel = null;
        }
    }

    _addPuzzleEventListeners() {
        this._puzzleModel.addEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
    }

    // Timer Management Methods
    startTimer() {
        this._timerRunning = true;
        this._attemptStartTime = this.elapsedTime;
        const timerValue = this._puzzleModel.time;
        this._availableTime = timerValue * 1000;
        this.updateTimer();
    }

    stopTimer() {
        this._timerRunning = false;
    }

    updateTimer() {
        const p = this.remainingTime / this._availableTime;
        this._lastTimerUpdate = this._elapsedTime;
        this.dispatchEvent(GameModel.PERCENT_TIMER, p);
    }

    timeout() {
        this._timerRunning = false;
        this.dispatchEvent(GameModel.TIMEOUT);
    }

    // Puzzle Interaction Methods
    selectItem(spot) {
        this.puzzle.pickSpot(spot);
        if (this._shouldTrack()) {
            this._trackingController.trackSpotSelected(this.puzzleNumber, spot.id, this.elapsedTime, this.attempt);
        }
    }

    deselectItem(spot) {
        this.puzzle.unpickSpot(spot);
        if (this._shouldTrack()) {
            this._trackingController.trackSpotDeselected(this.puzzleNumber, spot.id, this.elapsedTime, this.attempt);
        }
    }

    startPuzzleInteraction() {
        if (this._shouldTrack()) {
            this._trackingController.trackLevelStart(this.puzzleNumber, this.ruleId, this.elapsedTime, this.attempt);
        }
    }

    submitPuzzle(timeout = false) {
        const isSuccessful = this._puzzleModel.validate() && !timeout;
        this.lastPuzzleSuccessful = isSuccessful;
        this._puzzleSubmitted = true;

        if (this._shouldTrack()) {
            const submittedSequence = this._puzzleModel.pickedSpots.map(s => s.id);
            const correctSequence = this._puzzleModel.correctInput;
            const submittedBy = (timeout ? LevelEndType.TIMEOUT : LevelEndType.USER);

            this._trackingController.trackLevelSubmitted(
                this.puzzleNumber,
                this.ruleId,
                isSuccessful,
                submittedSequence,
                correctSequence,
                this.elapsedTime,
                this.attempt,
                submittedBy
            );
            this.dispatchEvent(GameModel.SUBMIT);
        }

        this._contextDispatcher.dispatch(SaveEvent.SAVE);
        return isSuccessful;
    }

    wasLastPuzzleSuccessful() {
        return this.lastPuzzleSuccessful;
    }

    // Save and Restore Methods
    getSaveData() {
        let status = this.status;
        let index = this.puzzleIndex;
        let sequenceIndex = this.sequenceIndex;

        if (this.status == GameStatus.RUNNING && this._puzzleSubmitted) {
            if (!this.lastPuzzleSuccessful) {
                if (this.canRetryPuzzle()) {
                    sequenceIndex++;
                } else if (!this.isPuzzleAllowFail()) {
                    status = GameStatus.END;
                } else if (this.hasNextPuzzle()) {
                    index++;
                    sequenceIndex = 0;
                }
            } else if (this.hasNextPuzzle()) {
                index++;
                sequenceIndex = 0;
            } else {
                status = GameStatus.END;
            }
        }

        return {
            elapsed: Math.round(this.elapsedTime),
            status,
            index,
            lastPuzzleSuccessful: this.lastPuzzleSuccessful,
            sequenceIndex,
            seed: this.seed
        };
    }

    restore(obj) {
        this.status = obj.status;
        this.elapsedTime = obj.elapsed;
        this.lastPuzzleSuccessful = obj.lastPuzzleSuccessful;
        this._tutorialAlreadySeen = true;
        this._restoredFeedbackPending = true;

        this._puzzleIndex = obj.index;
        this._sequenceIndex = obj.sequenceIndex;

        const urlSeed = getURLParameter("seed");
        this.seed = urlSeed !== null ? parseInt(urlSeed) : (obj.seed !== null && obj.seed !== undefined ? obj.seed : this.seed);
        console.warn("Restoring save game with seed: " + this.seed);

        // Re-generate puzzles based on savegame seed
        this._initializeConfig();
    }

    // Utility Methods
    hasNextPracticePuzzle() {
        return this._puzzleIndex < this._config.practicePuzzles.length - 1;
    }

    hasNextPuzzle() {
        return this._puzzleIndex < this._config.puzzles.length - 1;
    }

    isPuzzleAllowFail() {
        return this._puzzleModel !== undefined && this._puzzleModel.allowFail;
    }

    retryPuzzle() {
        if (this.canRetryPuzzle()) {
            this.initPuzzle(this._puzzleIndex, this._sequenceIndex + 1);
            return true;
        }
        return false;
    }

    canRetryPuzzle() {
        return !this._puzzleModel.allowFail && this._puzzleModel.hasNextSequence;
    }

    isPreviousPondDifferent() {
        if (this.status != GameStatus.PRACTICE && this.hasNextPuzzle() && this._puzzleIndex > 0) {
            return this._config.getPuzzle(this._puzzleIndex).location != this._config.getPuzzle(this._puzzleIndex - 1).location;
        }
        return false;
    }

    _shouldTrack() {
        return this.status != GameStatus.TUTORIAL && this.status != GameStatus.PRACTICE;
    }

    dispatchPlatformProgressEvent(progressValue) {
        this._trackingController.trackProgress(progressValue, this.elapsedTime);
    }

    // Getters and Setters
    get speedFactor() {
        return this._speedfactor;
    }

    get elapsedTime() {
        return this._elapsedTime;
    }

    set elapsedTime(value) {
        this._elapsedTime = value;
        this.dispatchEvent(GameModel.ELAPSED_TIME, this._elapsedTime);
    }

    get remainingTime() {
        return this._availableTime - (this._elapsedTime - this._attemptStartTime);
    }

    get status() {
        return this._status;
    }

    set status(value) {
        this._status = value;
        this.dispatchEvent(GameModel.STATUS, value);
    }

    get puzzle() {
        return this._puzzleModel;
    }

    get puzzleIndex() {
        return this._puzzleIndex;
    }

    get puzzleNumber() {
        return this._puzzleIndex + 1;
    }

    get sequenceIndex() {
        return this._sequenceIndex;
    }

    get ruleId() {
        let ruleId = this._puzzleModel.ruleId;
        if (ruleId.indexOf("locatePattern") == 0) {
            ruleId = "locatePattern";
        }
        return ruleId;
    }

    get attempt() {
        return this._sequenceIndex + 1;
    }

    set restoredFeedbackPending(bool) {
        this._restoredFeedbackPending = bool;
    }

    get restoredFeedbackPending() {
        return this._restoredFeedbackPending;
    }

    get seed() {
        return this._seed;
    }

    set seed(seed) {
        this._seed = seed;
        // Overriding Math.random with seeded PRNG
        Math.randomOrig = Math.random;
        Math.random = getSeededPRNG(this._seed);
    }

    set tutorialAlreadySeen(bool) {
        this._tutorialAlreadySeen = bool;
    }

    get tutorialAlreadySeen() {
        return this._tutorialAlreadySeen;
    }
}

// Static Event Constants
GameModel.STATUS = "GameModel.STATUS";
GameModel.PUZZLE = "GameModel.PUZZLE";
GameModel.SUBMIT = "GameModel.SUBMIT";
GameModel.ELAPSED_TIME = "GameModel.TIME";
GameModel.PERCENT_TIMER = "GameModel.PERCENT_TIMER";
GameModel.TIMEOUT = "GameModel.TIMEOUT";
GameModel.TUTORIAL_SEEN = "GameModel.TUTORIAL_SEEN";
GameModel.CLOSE_TUTORIAL_POPUP = "GameModel.CLOSE_TUTORIAL_POPUP";
GameModel.SHOW_SOLUTION_INFO = "GameModel.SHOW_SOLUTION_INFO";
GameModel.CONFIG_LOADED = "GameModel.CONFIG_LOADED";
